import * as React from 'react';
import { styled } from '@linaria/react';
import { Globe, Briefcase, Calendar, Hash } from 'lucide-react';

import { random } from '@/utils';
import {
  generateFullName,
  generateHandle,
  generateLocation,
  generateJob,
} from '@/helpers/fake-data.helpers';

import Paper from '@/components/Paper';

import ProfileCardActions from './ProfileCardActions';

const BACKDROP_COLORS: Array<string> = [
  '#F4A1C1',
  '#B588EF',
  '#7EE5E6',
  '#A3E7AC',
  '#EDEB97',
  '#F9BCAF',
];

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  avatarIndex?: number;
  avatarBackdropIndex?: number;
  name?: string;
  handle?: string;
  city?: string;
  country?: string;
  job?: string;
}

function ProfileCard({
  avatarBackdropIndex: providedAvatarBackdropIndex,
  avatarIndex: providedAvatarIndex,
  name: providedName,
  handle: providedHandle,
  city: providedCity,
  country: providedCountry,
  job: providedJob,
  ...delegated
}: Props) {
  const [avatarBackdropIndex] = React.useState<number>(
    () =>
      providedAvatarBackdropIndex ??
      random(0, BACKDROP_COLORS.length - 1)
  );
  const [avatarIndex] = React.useState<number>(
    () => providedAvatarIndex ?? random(1, 16)
  );
  const [name] = React.useState<string>(
    () => providedName ?? generateFullName()
  );
  const [handle] = React.useState<string>(
    () => providedHandle ?? generateHandle(name)
  );
  const [[city, country]] = React.useState<[string, string]>(() =>
    providedCity && providedCountry
      ? [providedCity, providedCountry]
      : generateLocation()
  );
  const [job] = React.useState<string>(
    () => providedJob ?? generateJob()
  );
  const [joinDate] = React.useState(() => {
    return new Date(random(2010, 2025), random(0, 11), random(1, 28));
  });
  const [numOfPosts] = React.useState(() => {
    return random(2, 2345);
  });

  const avatarSrc = `/images/peeps/${String(avatarIndex).padStart(3, '0')}.png`;
  const avatarBackdropColor =
    BACKDROP_COLORS[avatarBackdropIndex] ?? BACKDROP_COLORS[0];

  return (
    <Wrapper {...delegated}>
      <AvatarWrapper style={{ '--bg': avatarBackdropColor }}>
        <Avatar alt="" src={avatarSrc} />
      </AvatarWrapper>
      <NameAndHandle>
        <Name>{name}</Name>
        <Handle>@{handle}</Handle>
      </NameAndHandle>
      <Deets>
        <Item>
          <Globe size={18} />
          <TextClamp>
            {city}, {country}
          </TextClamp>
        </Item>
        <Item>
          <Briefcase size={18} />
          <TextClamp>{job}</TextClamp>
        </Item>
        <Item>
          <Calendar size={18} />
          {joinDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </Item>
        <Item>
          <Hash size={18} />
          {numOfPosts} Posts
        </Item>
      </Deets>

      <ProfileCardActions />
    </Wrapper>
  );
}

const Wrapper = styled(Paper)`
  position: relative;
  display: grid;
  grid-template-areas:
    'avatar'
    'name'
    'deets'
    'actions';
  justify-items: center;
  padding: 16px;
  gap: 16px;
  filter: drop-shadow(0px 1px 2px hsl(0deg 0% 0% / 0.2))
    drop-shadow(0px 3px 6px hsl(0deg 0% 0% / 0.2));

  && {
    border-radius: 8px;
    background: white;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 110px;
    border-radius: 8px 8px 0 0;
    background: var(--syntax-bg);
  }

  @container (min-width: 20rem) {
    grid-template-columns: 125px 1fr;
    grid-template-areas:
      'avatar  name'
      'deets   deets'
      'actions actions';
  }
`;

const AvatarWrapper = styled.div`
  grid-area: avatar;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  width: 125px;
  height: 125px;
  border-radius: 50%;
  background-color: var(--bg);
  overflow: hidden;
  overflow: clip;
  border: 3px solid var(--bg);
  outline: 6px solid white;

  @container (min-width: 20rem) {
    outline: 6px solid var(--syntax-bg);
    transform: translateY(-24px);
  }
`;

const Avatar = styled.img`
  display: block;
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  animation:
    riseIn 800ms cubic-bezier(0.17, 0.67, 0.51, 0.99),
    fadeIn 400ms;

  @keyframes riseIn {
    from {
      transform: translateY(16px);
    }
  }
`;

const NameAndHandle = styled.p`
  grid-area: name;
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: center;
  min-width: 0;

  @container (min-width: 20rem) {
    width: 100%;
    align-items: flex-start;
    padding-top: 0.75rem;
    text-align: left;
  }
`;

const Name = styled.span`
  display: block;
  font-size: 1.125rem;
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;

  @container (min-width: 20rem) {
    font-size: 1.25rem;
  }
`;

const Handle = styled.span`
  display: block;
  color: var(--color-gray-700);
  font-size: 1rem;
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const Deets = styled.ul`
  --gap: 8px;
  grid-area: deets;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--gap);
  padding: 0;
  color: var(--color-gray-900);
  min-width: 0;
  width: 100%;

  @container (min-width: 20rem) {
    /* NOTE: I can't use "1fr 1fr" because then the columns dynamically resize based on content, and I don't want them to do this. */
    grid-template-columns:
      calc(50% - var(--gap) / 2)
      calc(50% - var(--gap) / 2);
  }
`;

const Item = styled.li`
  display: flex;
  gap: 12px;
  align-items: center;
  font-size: 1rem;

  svg {
    flex-shrink: 0;
  }

  @container (max-width: 20rem) {
    &:nth-child(n + 3) {
      display: none;
    }
  }
`;

const TextClamp = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

export default ProfileCard;
